/**
 * API Response Cache
 * Caches API responses to reduce redundant requests and work around intermittent auth issues
 * Uses localStorage for persistence across page refreshes
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class ApiCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes default
  private maxStaleAge = 24 * 60 * 60 * 1000; // 24 hours
  private storageKey = 'api_cache_v1';
  private initialized = false;

  /**
   * Initialize cache from localStorage
   */
  private init() {
    if (this.initialized) return;
    this.initialized = true;

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const entries = JSON.parse(stored) as Array<[string, CacheEntry<any>]>;
        const now = Date.now();
        let validCount = 0;
        let staleCount = 0;
        let droppedCount = 0;

        entries.forEach(([key, entry]) => {
          const isFresh = now <= entry.expiresAt;
          const isWithinMaxStale = now - entry.timestamp <= this.maxStaleAge;

          if (isFresh || isWithinMaxStale) {
            this.cache.set(key, entry);
            if (isFresh) validCount++;
            else staleCount++;
          } else {
            droppedCount++;
          }
        });

        console.log(`[Cache] Restored ${validCount} fresh + ${staleCount} stale entries (${droppedCount} dropped)`);
      }
    } catch (e) {
      console.warn('[Cache] Failed to restore from localStorage:', e);
    }
  }

  /**
   * Persist cache to localStorage
   */
  private persist() {
    try {
      const now = Date.now();
      // prune very old entries before persisting
      for (const [key, entry] of this.cache.entries()) {
        if (now - entry.timestamp > this.maxStaleAge) {
          this.cache.delete(key);
        }
      }

      const entries = Array.from(this.cache.entries());
      localStorage.setItem(this.storageKey, JSON.stringify(entries));
    } catch (e) {
      console.warn('[Cache] Failed to persist to localStorage:', e);
    }
  }

  /**
   * Get cached data if available and not expired
   */
  get<T>(key: string): T | null {
    this.init();
    
    const entry = this.cache.get(key);
    if (!entry) {
      console.log('[Cache] Miss:', key);
      return null;
    }

    const now = Date.now();
    if (now > entry.expiresAt) {
      console.log('[Cache] Expired:', key);
      return null;
    }

    console.log('[Cache] Hit:', key, '(age:', Math.round((now - entry.timestamp) / 1000), 'seconds)');
    return entry.data as T;
  }

  /**
   * Get cached data even if expired, as long as it's not too old.
   * Useful as a fallback when the network temporarily fails.
   */
  getStale<T>(key: string): T | null {
    this.init();

    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > this.maxStaleAge) {
      this.cache.delete(key);
      this.persist();
      return null;
    }

    console.log('[Cache] Stale hit:', key, '(age:', Math.round((now - entry.timestamp) / 1000), 'seconds)');
    return entry.data as T;
  }

  /**
   * Set cache entry with optional TTL
   */
  set<T>(key: string, data: T, ttl?: number): void {
    this.init();
    
    const now = Date.now();
    const expiresAt = now + (ttl || this.defaultTTL);
    
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt,
    });
    
    this.persist();
    
    console.log('[Cache] Set:', key, '(TTL:', Math.round((ttl || this.defaultTTL) / 1000), 'seconds)');
  }

  /**
   * Invalidate specific cache entry
   */
  invalidate(key: string): void {
    this.init();
    console.log('[Cache] Invalidate:', key);
    this.cache.delete(key);
    this.persist();
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern: string): void {
    this.init();
    console.log('[Cache] Invalidate pattern:', pattern);
    const keysToDelete: string[] = [];
    
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
    this.persist();
  }

  /**
   * Clear all cache
   */
  clear(): void {
    console.log('[Cache] Clear all');
    this.cache.clear();
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      console.warn('[Cache] Failed to clear localStorage:', e);
    }
  }

  /**
   * Get cache stats
   */
  getStats() {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    
    return {
      totalEntries: entries.length,
      validEntries: entries.filter(([_, entry]) => now <= entry.expiresAt).length,
      expiredEntries: entries.filter(([_, entry]) => now > entry.expiresAt).length,
      keys: entries.map(([key]) => key),
    };
  }
}

export const apiCache = new ApiCache();
